import { useCallback, useEffect, useState, type FormEvent } from 'react';

import {
  ApiError,
  discardReport,
  generateReport,
  getReport,
  getReportIssue,
  issueReport,
  listReports,
  updateReportNarrative,
  type ReportContent,
  type ReportIssueDetail,
  type ReportSummary,
  type ReportView,
} from '../api.js';
import { formatDate, formatMoney } from '../labels.js';

/**
 * The weekly report — gathered, not written.
 *
 * Every figure a report shows came from the registers on this same page, so
 * the report can never say one thing while the page says another. The PM
 * writes two paragraphs (the narrative); everything else is the week as the
 * records tell it, and the GAPS ARE NAMED — "3 of 5 site days have no diary
 * entry" is information the reader deserves, not something to smooth over.
 *
 * Issuing is append-only: version 1 is what went out, forever. Reissuing
 * writes version 2 beside it. "View as sent" shows a version exactly as it
 * left the building.
 */

interface Props {
  readonly projectId: string;
  readonly currency: string;
  /** Decided by the server. Issuing is `report:approve` — sending the week
   *  out is a decision, and an external role holds no buttons at all. */
  readonly can: {
    readonly generate: boolean;
    readonly edit: boolean;
    readonly issue: boolean;
  };
}

const STATUS_LABELS: Readonly<Record<string, string>> = {
  draft: 'Draft',
  issued: 'Issued',
};

export function Reports({ projectId, currency, can }: Props): JSX.Element {
  const [items, setItems] = useState<readonly ReportSummary[] | null>(null);
  /** A denied read is not an empty list and not a failure. */
  const [denied, setDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [view, setView] = useState<ReportView | null>(null);
  /** One issued version, exactly as it went out, when the reader asks. */
  const [asSent, setAsSent] = useState<ReportIssueDetail | null>(null);

  const load = useCallback(async () => {
    try {
      /*
       * The rows only, and no "load the rest" affordance on this one.
       *
       * The server bounds this register like every other, which is the safety
       * that matters. But it grows ONE ROW A WEEK — the default page is ten
       * years of reports — so a notice about a truncated load would be
       * furniture on a screen that will never show it. Same judgement the
       * diary got, for the same reason.
       */
      setItems((await listReports(projectId)).rows);
      setDenied(false);
    } catch (caught) {
      const refused = caught instanceof ApiError && (caught.status === 403 || caught.status === 404);
      setDenied(refused);
      setError(refused ? null : 'Could not load the reports.');
      setItems([]);
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  const openReport = useCallback(
    async (reportId: string) => {
      setOpenId(reportId);
      setView(null);
      setAsSent(null);
      setError(null);
      try {
        setView(await getReport(projectId, reportId));
      } catch (caught) {
        setOpenId(null);
        setError(caught instanceof ApiError ? caught.message : 'Could not open the report.');
      }
    },
    [projectId],
  );

  async function run(work: () => Promise<unknown>, reopen?: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await work();
      await load();
      if (reopen) await openReport(reopen);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  const head = (
    <div className="section-head sub-head-row">
      <h3 className="sub-head">Weekly reports</h3>
      {items && can.generate && (
        <button
          type="button"
          className="inline"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              const created = await generateReport(projectId);
              await load();
              await openReport(created.id);
            } catch (caught) {
              setError(caught instanceof ApiError ? caught.message : 'That did not work.');
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? 'Gathering…' : 'Generate this week'}
        </button>
      )}
    </div>
  );

  if (denied) {
    return (
      <>
        {head}
        <p className="hint">Your role does not include this project&rsquo;s reports.</p>
      </>
    );
  }

  if (items === null) {
    return (
      <>
        {head}
        <p aria-live="polite">Loading the reports…</p>
      </>
    );
  }

  return (
    <>
      {head}

      {error && (
        <div className="alert" role="alert">
          <span>{error}</span>
        </div>
      )}

      {items.length === 0 ? (
        <div className="empty">
          <p>
            <strong>No reports yet.</strong>
          </p>
          {can.generate && (
            <p>
              Generate one and the week is gathered from this page&rsquo;s own registers —
              nothing is retyped, and any gaps are named.
            </p>
          )}
        </div>
      ) : (
        <table className="grid">
          <caption className="visually-hidden">Weekly reports</caption>
          <thead>
            <tr>
              <th scope="col">Period</th>
              <th scope="col">Status</th>
              <th scope="col">Versions sent</th>
              <th scope="col">Generated by</th>
              <th scope="col">
                <span className="visually-hidden">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((report) => (
              <tr key={report.id}>
                <td data-label="Period">
                  {formatDate(report.period_start)} – {formatDate(report.period_end)}
                </td>
                <td data-label="Status">{STATUS_LABELS[report.status] ?? report.status}</td>
                <td data-label="Versions sent">
                  {report.issue_count === 0 ? '—' : report.issue_count}
                </td>
                <td data-label="Generated by">{report.generated_by_name ?? '—'}</td>
                <td className="row-actions">
                  <button
                    type="button"
                    className="link-button"
                    disabled={busy}
                    onClick={() => {
                      if (openId === report.id) {
                        setOpenId(null);
                        setView(null);
                        setAsSent(null);
                      } else {
                        void openReport(report.id);
                      }
                    }}
                  >
                    {openId === report.id ? 'Close' : 'Open'}
                    <span className="visually-hidden">
                      {' '}
                      report {formatDate(report.period_start)} – {formatDate(report.period_end)}
                    </span>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {openId && view === null && !denied && items.length > 0 && (
        <p aria-live="polite">Opening the report…</p>
      )}

      {view && (
        <ReportPanel
          view={view}
          asSent={asSent}
          currency={currency}
          busy={busy}
          can={can}
          onViewAsSent={async (issueId) => {
            setError(null);
            try {
              setAsSent(await getReportIssue(projectId, issueId));
            } catch (caught) {
              setError(caught instanceof ApiError ? caught.message : 'Could not open that version.');
            }
          }}
          onBackToDraft={() => setAsSent(null)}
          onSaveNarrative={(input) =>
            run(() => updateReportNarrative(projectId, view.id, input), view.id)
          }
          onIssue={() => void run(() => issueReport(projectId, view.id), view.id)}
          onDiscard={() =>
            void run(async () => {
              await discardReport(projectId, view.id);
              setOpenId(null);
              setView(null);
            })
          }
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

interface PanelProps {
  readonly view: ReportView;
  readonly asSent: ReportIssueDetail | null;
  readonly currency: string;
  readonly busy: boolean;
  readonly can: Props['can'];
  readonly onViewAsSent: (issueId: string) => void | Promise<void>;
  readonly onBackToDraft: () => void;
  readonly onSaveNarrative: (input: { summary?: string; next_week?: string }) => void | Promise<void>;
  readonly onIssue: () => void;
  readonly onDiscard: () => void;
}

function ReportPanel({
  view,
  asSent,
  currency,
  busy,
  can,
  onViewAsSent,
  onBackToDraft,
  onSaveNarrative,
  onIssue,
  onDiscard,
}: PanelProps): JSX.Element {
  const content = asSent ? asSent.content : view.content;
  const isDraft = view.status === 'draft';

  return (
    <div className="panel">
      <h4>
        Report {formatDate(view.period_start)} – {formatDate(view.period_end)}
        {asSent
          ? ` · version ${asSent.version_no}, as sent ${formatDate(asSent.issued_at)}`
          : isDraft
            ? ' · draft'
            : ''}
      </h4>

      {asSent && (
        <p className="hint">
          This is version {asSent.version_no} exactly as it went out
          {asSent.issued_by_name ? `, sent by ${asSent.issued_by_name}` : ''}.{' '}
          <button type="button" className="link-button" onClick={onBackToDraft}>
            Back to the current report
          </button>
        </p>
      )}

      {/* J9: the holes, named. This block comes FIRST — a report that leads
          with clean numbers and buries its gaps is the failure mode. */}
      {content.missing.length > 0 && (
        <div className="callout">
          <p>
            <strong>Gaps this period</strong>
          </p>
          <ul>
            {content.missing.map((gap) => (
              <li key={gap}>{gap}</li>
            ))}
          </ul>
        </div>
      )}

      <h5 className="sub-head">The week, from the registers</h5>
      <dl className="detail-grid">
        <dt>Project health</dt>
        <dd>
          {content.pulse.overall} · {content.pulse.measured} of {content.pulse.signals.length}{' '}
          signals measured
        </dd>
        <dt>Site diaries</dt>
        <dd>
          {content.site.diaries_submitted} of {content.site.working_days} working days
        </dd>
        <dt>Progress entries</dt>
        <dd>{content.site.progress_entries}</dd>
        <dt>Tasks finished</dt>
        <dd>{content.site.tasks_done}</dd>
        <dt>Drawings issued</dt>
        <dd>{content.design.revisions_issued}</dd>
        <dt>Submittals decided</dt>
        <dd>
          {content.design.submittals_decided}
          {content.design.submittals_overdue > 0
            ? ` (${content.design.submittals_overdue} overdue)`
            : ''}
        </dd>
        <dt>Deliveries</dt>
        <dd>
          {content.procurement.deliveries}
          {content.procurement.deliveries_with_damage > 0
            ? ` (${content.procurement.deliveries_with_damage} with damage)`
            : ''}
        </dd>
        <dt>Inspections concluded</dt>
        <dd>{content.quality.inspections_concluded}</dd>
        <dt>NCRs</dt>
        <dd>
          {content.quality.ncrs_raised} raised, {content.quality.ncrs_closed} closed
        </dd>
        <dt>Snags</dt>
        <dd>
          {content.quality.snags_raised} raised, {content.quality.snags_closed} closed
        </dd>
        <dt>Milestones achieved</dt>
        <dd>{content.milestones.achieved}</dd>
      </dl>

      {/* ABSENT for a caller without finance:view — no row, not a blank. */}
      {content.commercial && content.commercial.totals && (
        <>
          <h5 className="sub-head">Commercial position</h5>
          <dl className="detail-grid">
            <dt>Allowed</dt>
            <dd className="mono">
              {currency} {formatMoney(content.commercial.totals.allowed)}
            </dd>
            <dt>Committed</dt>
            <dd className="mono">
              {currency} {formatMoney(content.commercial.totals.committed)}
            </dd>
            <dt>Spent</dt>
            <dd className="mono">
              {currency} {formatMoney(content.commercial.totals.spent)}
            </dd>
            <dt>Forecast</dt>
            <dd className="mono">
              {currency} {formatMoney(content.commercial.totals.forecast)}
            </dd>
          </dl>
        </>
      )}

      {asSent ? (
        <>
          <h5 className="sub-head">Narrative</h5>
          <p>{content.narrative.summary || 'Nothing was written for this version.'}</p>
          {content.narrative.next_week && <p>{content.narrative.next_week}</p>}
        </>
      ) : isDraft && can.edit ? (
        <NarrativeForm
          key={view.id}
          narrative={content.narrative}
          busy={busy}
          onSave={onSaveNarrative}
        />
      ) : (
        <>
          <h5 className="sub-head">Narrative</h5>
          <p>{content.narrative.summary || 'Nothing written yet.'}</p>
          {content.narrative.next_week && <p>{content.narrative.next_week}</p>}
        </>
      )}

      {!asSent && (can.issue || (isDraft && can.edit)) && (
        <div className="button-row">
          {can.issue && (
            <button type="button" className="inline" disabled={busy} onClick={onIssue}>
              {view.issue_count === 0
                ? 'Issue this report'
                : `Issue again (version ${view.issue_count + 1})`}
            </button>
          )}
          {isDraft && can.edit && (
            <button type="button" className="secondary inline danger" disabled={busy} onClick={onDiscard}>
              Discard draft
            </button>
          )}
        </div>
      )}

      {view.issues.length > 0 && !asSent && (
        <>
          <h5 className="sub-head">Versions sent</h5>
          <ul>
            {view.issues.map((issue) => (
              <li key={issue.id}>
                Version {issue.version_no}, sent {formatDate(issue.issued_at)}
                {issue.issued_by_name ? ` by ${issue.issued_by_name}` : ''}{' '}
                <button
                  type="button"
                  className="link-button"
                  disabled={busy}
                  onClick={() => void onViewAsSent(issue.id)}
                >
                  View as sent<span className="visually-hidden"> version {issue.version_no}</span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

interface NarrativeProps {
  readonly narrative: ReportContent['narrative'];
  readonly busy: boolean;
  readonly onSave: (input: { summary?: string; next_week?: string }) => void | Promise<void>;
}

/** The two fields a person writes. The figures above have no form on purpose. */
function NarrativeForm({ narrative, busy, onSave }: NarrativeProps): JSX.Element {
  const [summary, setSummary] = useState(narrative.summary);
  const [nextWeek, setNextWeek] = useState(narrative.next_week);

  function submit(event: FormEvent): void {
    event.preventDefault();
    void onSave({ summary, next_week: nextWeek });
  }

  return (
    <form onSubmit={submit}>
      <h5 className="sub-head">Narrative</h5>
      <div className="field">
        <label htmlFor="report-summary">This week, in your words</label>
        <textarea
          id="report-summary"
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          rows={3}
          maxLength={8000}
          disabled={busy}
        />
      </div>
      <div className="field">
        <label htmlFor="report-next-week">Next week</label>
        <textarea
          id="report-next-week"
          value={nextWeek}
          onChange={(e) => setNextWeek(e.target.value)}
          rows={3}
          maxLength={8000}
          disabled={busy}
        />
      </div>
      <div className="button-row">
        <button type="submit" className="inline" disabled={busy}>
          {busy ? 'Saving…' : 'Save narrative'}
        </button>
      </div>
    </form>
  );
}
